/**
 * de-date.adapter.ts
 * -----------------------------------------------------------------------------
 * Robuster deutscher DateAdapter für Angular-19-Material-Datepicker, gebaut auf
 * dem bereits installierten `date-fns@2.30` (KEINE neue Dependency, insbesondere
 * NICHT `@angular/material-date-fns-adapter`).
 *
 * Warum dieser Adapter existiert
 * ------------------------------
 * Der eingebaute `NativeDateAdapter` parst über `Date.parse()`. `Date.parse()`
 * interpretiert die deutsche Eingabe "04.05.2026" (= 4. Mai) als US-Format
 * MM.DD.YYYY und macht daraus den 5. April (bzw. einen Invalid Date). Außerdem
 * rendert er über `Intl.DateTimeFormat`, was im de-DE-Locale "6.4.2026" liefert
 * statt des gewünschten festen "dd.MM.yyyy". Dieser Adapter ersetzt Parse +
 * Format durch deterministische, locale-treue date-fns-Logik und behebt damit
 * das im Bug-Report beschriebene Day/Month-Swap-Verhalten.
 *
 * Angular-19-Detail
 * -----------------
 * In Angular 19 hat `NativeDateAdapter` einen PARAMETERLOSEN Konstruktor und
 * liest `MAT_DATE_LOCALE` selbst via `inject()`. Diese Subklasse braucht daher
 * keinen eigenen Konstruktor und keine `deps`-Angabe — die Locale-Auflösung
 * passiert in der Basisklasse (sofern im Injection-Context instanziiert).
 */

import { Injectable, Provider } from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatDateFormats,
  NativeDateAdapter,
} from '@angular/material/core';

import { format as dfFormat, isValid as dfIsValid, parse as dfParse } from 'date-fns';
// In date-fns v2 wird die deutsche Locale als benannter Export aus dem
// 'date-fns/locale'-Barrel geladen (verifiziert gegen node_modules/date-fns).
import { de as deLocale } from 'date-fns/locale';
import type { Locale } from 'date-fns';

/**
 * Akzeptierte Eingabe-Formate beim Tippen ins Feld, in Prioritäts-Reihenfolge.
 * Die spezifischsten/strengsten zuerst, damit z. B. "04.05.2026" als
 * `dd.MM.yyyy` und nicht versehentlich als 2-stellige Variante greift.
 */
const DE_PARSE_FORMATS: readonly string[] = [
  'dd.MM.yyyy',
  'd.M.yyyy',
  'dd.MM.yy',
  'd.M.yy',
  'yyyy-MM-dd',
];

@Injectable()
export class DeDateAdapter extends NativeDateAdapter {
  // Wir erweitern NativeDateAdapter (nicht die abstrakte DateAdapter-Basis),
  // damit alle Kalender-Methoden (getYear, addCalendarDays, isValid, invalid,
  // clone, …) sowie die Intl-basierten Namens-Helfer (getMonthNames etc.)
  // geerbt werden. Nur parse() + format() werden überschrieben.
  // Kein eigener Konstruktor nötig: Angular-19-NativeDateAdapter ist
  // parameterlos und löst MAT_DATE_LOCALE intern via inject() auf.

  /** date-fns-Locale für Parse + Format (aktuell fix Deutsch). */
  private readonly _dfLocale: Locale = deLocale;

  /**
   * Parst Benutzereingaben robust gegen deutsche Formate.
   *
   * Verhalten:
   *  - null / leer / nur Whitespace     -> null  (Material: "kein Datum")
   *  - number                           -> Date aus Timestamp
   *  - Date                             -> Klon
   *  - String, der zu einem der
   *    DE_PARSE_FORMATS passt           -> exaktes Datum (lokale Mitternacht)
   *  - ISO-ähnlicher String             -> Date.parse-Fallback
   *  - sonst                            -> Invalid Date (NIE stilles Day/Month-Swap)
   *
   * Anti-Swap-Härtung: Die Sicherheit kommt aus der Format-Schleife plus einer
   * Round-Trip-Prüfung (re-format mit demselben Pattern muss die Eingabe exakt
   * reproduzieren). Das verhindert Overflow-Rolling (z. B. Tag 32) und
   * Teil-Matches, die sonst stillschweigend Tag/Monat verschieben würden.
   */
  override parse(value: unknown, _parseFormat?: unknown): Date | null {
    if (value == null) {
      return null;
    }
    if (typeof value === 'number') {
      return new Date(value);
    }
    if (value instanceof Date) {
      return this.clone(value);
    }
    if (typeof value !== 'string') {
      // Unbekannter Typ -> Invalid Date (Material zeigt Validierungsfehler),
      // statt zu raten.
      return this.invalid();
    }

    const raw = value.trim();
    if (raw.length === 0) {
      return null;
    }

    const reference = new Date();

    for (const fmt of DE_PARSE_FORMATS) {
      const parsed = dfParse(raw, fmt, reference, { locale: this._dfLocale });
      if (!dfIsValid(parsed)) {
        continue;
      }
      // Round-Trip-Verifikation: re-format mit demselben Pattern muss die
      // Eingabe ergeben (raw ist bereits getrimmt, date-fns fügt kein Padding/
      // Whitespace hinzu). Verhindert Overflow-Rolling und Teil-Matches.
      if (dfFormat(parsed, fmt, { locale: this._dfLocale }) === raw) {
        return parsed;
      }
    }

    // Letzter Ausweg NUR für echte ISO-8601-Strings (z. B. mit Zeit-Anteil),
    // die die `yyyy-MM-dd`-Runde oben nicht abdeckt. Bewusst eng begrenzt:
    // ein UNVOLLSTÄNDIGES deutsches Datum wie "04.05" darf NICHT über
    // `Date.parse` zu einem Zufallsdatum (5. April 2001) werden, sondern muss
    // Invalid bleiben, damit der Datepicker einen Validierungsfehler zeigt.
    if (/^\d{4}-\d{2}-\d{2}([T ].*)?$/.test(raw)) {
      const fallback = new Date(Date.parse(raw));
      if (this.isValid(fallback)) return fallback;
    }
    return this.invalid();
  }

  /**
   * Rendert ein Datum.
   *
   * `displayFormat` kommt aus DE_DATE_FORMATS und ist immer ein date-fns-
   * Pattern-String (z. B. 'dd.MM.yyyy', 'MMM yyyy', 'MMMM yyyy'). Ein
   * Intl.DateTimeFormat-Options-Objekt (falls dieser Adapter je mit den
   * nativen MAT_NATIVE_DATE_FORMATS kombiniert würde) fällt an die Basis zurück.
   */
  override format(date: Date, displayFormat: unknown): string {
    if (!this.isValid(date)) {
      throw Error('DeDateAdapter: Cannot format invalid date.');
    }
    if (typeof displayFormat === 'string' && displayFormat.length > 0) {
      return dfFormat(date, displayFormat, { locale: this._dfLocale });
    }
    // Fallback: Intl-Options-Objekt o. ä. -> an NativeDateAdapter delegieren.
    return super.format(date, displayFormat as object);
  }
}

/**
 * Feste deutsche Anzeige-/Parse-Formate. `dateInput` für Parse UND Display ist
 * 'dd.MM.yyyy' (z. B. "04.05.2026"). Die Label-/A11y-Formate nutzen date-fns-
 * Pattern-Strings, die unsere `format()`-Override versteht.
 */
export const DE_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'dd.MM.yyyy',
  },
  display: {
    dateInput: 'dd.MM.yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd.MM.yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

/**
 * Provider-Helper: aktiviert DeDateAdapter + deutsche Formate. Zentral in
 * `main.ts` in die bootstrapApplication-providers spreaden:
 *
 *   providers: [ ...provideDeDateAdapter(), ... ]
 *
 * Voraussetzung: MAT_DATE_LOCALE ist auf 'de-DE' gesetzt (siehe main.ts) und
 * die Datepicker-Komponenten importieren MatDatepickerModule, aber NICHT mehr
 * MatNativeDateModule — letzteres würde via Element-Injector den hier am Root
 * registrierten DateAdapter/MAT_DATE_FORMATS überschatten.
 */
export function provideDeDateAdapter(): Provider[] {
  return [
    { provide: DateAdapter, useClass: DeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DE_DATE_FORMATS },
  ];
}
